import React, { ReactElement } from 'react';
import {
  Component,
  Position,
  PuzzleButton,
  WithTooltip,
  Container, HBox
} from 'puzzle';
import { PuzzleTooltip } from "puzzle/components";
import classNames from "classnames";
import { ASSIGNEE_USER, STATUS_CARD_PHASE, STATUS_CARD_PROPS, STATUS_CARD_STATE } from "./Types";
import { getSubmissionPhaseStatus } from "../../../util/Util";
import _ from "lodash";
import { EMAIL_CLIENT_PARAMS } from "../../../util/OpenMailLink/Types";
import OpenMailLink from "../../../util/OpenMailLink/OpenMailLink";

class StatusCard extends Component<STATUS_CARD_PROPS, STATUS_CARD_STATE> {
  tooltip: any;

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    let { phase } = this.props;
    const phaseOverDue: boolean = new Date().getTime() >= phase.phaseDueDate;
    this.setState({
      phaseOverDue
    });
  }

  /**
   * Return status files in error element
   */
  generateInErrorFilesContent = (phase: STATUS_CARD_PHASE, fileCount: number, fileCounts: string[]): ReactElement => {
    return (
      <span className={'tp-submission-files'} title={this.generateTooltipTitle(phase, fileCounts)}>
        {fileCount}
      </span>
    );
  };
  /**
   * Generate string representing automatic phases' file count
   */
  generateTooltipTitle = (phase: STATUS_CARD_PHASE, fileCounts: string[]): string => {
    const fileCountPairs: string[] = fileCounts
      .map((count: string) => `${this.translate(count)}: ${(phase.fileCount as any)[count]}`);
    return fileCountPairs.join('\n');
  };

  /**
   * Return status files element
   */
  generateStatusFilesContent = (phase: STATUS_CARD_PHASE, fileCount: number, fileCounts: string[]): ReactElement => {
    return (
      <HBox className={'k-cursor-not-allowed k-gap-x-.5'}>
        <i role={'presentation'} className={'tp-icon tp-icon-xs tp-file-open tp-color-disabled'} />
        <span className={'tp-font-size-sm'} title={this.generateTooltipTitle(phase, fileCounts)}>
        {fileCount}
      </span>
      </HBox>
    );
  };

  /**
   * Get assignee user
   * @param {STATUS_CARD_PHASE} phase
   */
  getAssignee = (phase: STATUS_CARD_PHASE) => {
    let { users } = phase;
    let { submissionId, submissionName } = this.props.submissionData;

    const emailClientParams: EMAIL_CLIENT_PARAMS = {
      recipients: users.map(user => user.email),
      subject: `${submissionId} ${submissionName}`,
      message: ''
    }

    let calculatedPhaseStatus: string = getSubmissionPhaseStatus(phase);
    let cls: string = classNames({
      'tp-auto-step': phase.batchTask,
      'tp-human-step tp-text-ellipsis': !phase.batchTask,
      'tp-not-claimed': !phase.batchTask && users.length === 0,
      'tp-completed': calculatedPhaseStatus === 'COMPLETED',
      'tp-in-progress': calculatedPhaseStatus === 'IN_PROGRESS',
    });


    let component;
    switch (users.length) {
      case 0:
        let notClaimed = (calculatedPhaseStatus === 'NOT_CLAIMED' || calculatedPhaseStatus === 'IN_PROGRESS') && !phase.batchTask;
        component = (
          <span className={cls}>
              {notClaimed ? this.translate('notClaimed') : this.translate('autoStep')}
            </span>);
        break;
      case 1:
        component = (
          <span className={cls}>
            <OpenMailLink
              emailClientParams={emailClientParams}
              policy={'hasHyperlinkAssigneeNamePolicy'}
            >
              {users[0].firstName + ' ' + users[0].lastName}
            </OpenMailLink>
            </span>
        );
        break;
      default:
        let tooltipTitle = users.map((user: ASSIGNEE_USER) => `${user.firstName} ${user.lastName}`).join('\n');

        let multipleSpan = <WithTooltip
          context={this}
          className={'k-d-flex-row k-align-items-center'}
          title={tooltipTitle}
          htmlTag={'span'}
        >
            <span
              className={`k-d-flex-row k-align-items-center tp-pointable ${cls}`}
              title={tooltipTitle}
            >
              {this.translate('multiple')}
            </span>
          <i
            aria-hidden="true"
            className='tp-icon tp-people tp-icon-sm tp-pointable'
            tabIndex={0}
            title={tooltipTitle}
          />
        </WithTooltip>

        component = (
          <OpenMailLink
            emailClientParams={emailClientParams}
            policy={'hasHyperlinkAssigneeNamePolicy'}
            titleText={tooltipTitle}
            tooltipPosition={'right'}
            withTooltip
          >
            {multipleSpan}
          </OpenMailLink>
        )
        break;
    }

    return component;
  }

  /**
   * Render date for human phases only
   */
  getStatusDate = (): string | null => {
    const isCancelledSubmission = this.props.submissionStatus === 'CANCELLED';
    const { phaseDueDate } = this.props.phase;
    let date: string | null = '';
    if (!isCancelledSubmission && phaseDueDate) {
      date = this.translateByNamespace('date.dateWithTime', { date: new Date(phaseDueDate) });
    }
    return date;
  };

  /**
   * Dispatch an action to navigate to files tab and prefilter the files grid
   */
  prefilterFilesTab = () => {
    const { phase, sectionName, subsectionName } = this.props;
    let filesFilter = {
      filters: {
        targetLanguageValues: [sectionName.locale],
        batchNameValues: [subsectionName],
        phaseNameValues: phase.phaseName !== 'Deliver' ? [phase.phaseName] : []
      },
      tab: {
        selected: 2
      }
    }
    this.dispatch('prefilterFiles', Position.end, { filesFilter });
  };

  /**
   * Render files for phases in progress only
   */
  statusFilesRenderer = (): ReactElement => {
    const phasesThatRenderStatusFileContent = ['IN_PROGRESS', 'ON_HOLD'];
    const { phase, sectionName, subsectionName } = this.props;
    let {
      cancelledFileCount,
      failedFileCount,
      inProcessFileCount,
      readyFileCount,
      reservedFileCount,
      deliveredFileCount
    } = phase.fileCount;

    let files: ReactElement | null = null;
    let inErrorFiles: ReactElement | null = null;
    const phaseInProgress: boolean = phasesThatRenderStatusFileContent.includes(getSubmissionPhaseStatus(phase));
    const isDeliverPhase: boolean = phase.phaseName === 'Deliver';

    // list of non error files to be displayed in tooltip
    const fileCounts: string[] = ['cancelledFileCount', 'inProcessFileCount', 'readyFileCount', 'reservedFileCount', 'deliveredFileCount'];
    // list of error files to be displayed in tooltip
    const inErrorFileCounts: string[] = ['cancelledFileCount', 'failedFileCount', 'reservedFileCount'];
    let fileCount: number = cancelledFileCount + inProcessFileCount + readyFileCount + reservedFileCount + deliveredFileCount;
    let inErrorFileCount: number = 0;

    if (phaseInProgress) {
      if (phase.batchTask) { // automatic step
        if (fileCount) {
          files = this.generateStatusFilesContent(phase, fileCount, fileCounts);
        }

        inErrorFileCount = cancelledFileCount + failedFileCount + reservedFileCount;
        if (inErrorFileCount) {
          inErrorFiles = this.generateInErrorFilesContent(phase, inErrorFileCount, inErrorFileCounts);
        }
      } else { // human step
        fileCount = inProcessFileCount + readyFileCount + cancelledFileCount;
        files = (
          <>
            {!!cancelledFileCount &&
              <>
                <span
                  className={'tp-files-cancelled tp-text-medium'}>{this.translate('filesCancelled', { count: cancelledFileCount })}</span>
              </>
            }
            {!!cancelledFileCount && !!fileCount &&
              <span className={'tp-color-disabled'}>{this.translate('slash')}</span>}
            <PuzzleButton
              aria-label={this.translate('SRL-filesBtnLabel', {
                count: fileCount,
                batch: subsectionName,
                language: sectionName.value,
                phase: phase.phaseName
              })}
              size={'small'}
              onClick={this.prefilterFilesTab}
              className={'tp-color-link'}
              iconClass={'tp-icon tp-file-open'}
              fillMode={'flat'}
              themeColor={'primary'}>
              {fileCount}
            </PuzzleButton>
          </>
        );
      }
    } else if ((isDeliverPhase && fileCount)) {
      files = (
        <>
          <PuzzleButton
            aria-label={this.translate('SRL-filesBtnLabel', {
              count: fileCount,
              batch: subsectionName,
              language: sectionName.value,
              phase: phase.phaseName
            })}
            onClick={this.prefilterFilesTab}
            size={'small'}
            className={'tp-color-link'}
            iconClass={'tp-icon tp-file-open'}
            fillMode={'flat'}
            themeColor={'primary'}>
            {fileCount}
          </PuzzleButton>
          {!!cancelledFileCount &&
            <span
              className={'tp-files-cancelled'}>{this.translate('filesCancelled', { count: cancelledFileCount })}</span>
          }
        </>
      );
    } else if (cancelledFileCount) {
      files = (<>
        <PuzzleButton
          aria-label={this.translate('SRL-filesBtnLabel', {
            count: fileCount,
            batch: subsectionName,
            language: sectionName.value,
            phase: phase.phaseName
          })}
          onClick={this.prefilterFilesTab}
          size={'small'}
          fillMode={'flat'}
          iconClass={'tp-icon tp-file-cancelled'}
          themeColor={'error'}>
          {cancelledFileCount}
        </PuzzleButton></>)
    }

    return (
      <Container
        alignItemsCenter
        hBox
        className='tp-submission-status-card-file-count'
        role="gridcell"
      >
        {!!inErrorFileCount && <span className={'tp-submission-files-wrapper'}>{inErrorFiles}</span>}
        {files}
      </Container>
    );
  };

  /**
   * Render for phase status progressbar
   */
  statusContent = (phase: STATUS_CARD_PHASE) => {
    const phaseState: string = getSubmissionPhaseStatus(phase);

    const { lastPhase, sectionStatus } = this.props;
    const phaseCardStatusCls: string = classNames(
      '',
      {
        'tp-not-claimed': phaseState === 'NOT_CLAIMED' && sectionStatus !== 'CANCELLED',
        'tp-completed': phaseState === 'COMPLETED' && !lastPhase,
        'tp-in-progress': phaseState === 'IN_PROGRESS' || phaseState === 'ON_HOLD',
        'tp-in-error': phase.phaseStatus === 'FAILED',
        'tp-last-completed': phaseState === 'COMPLETED' && lastPhase,
        'tp-cancelled': phaseState === 'CANCELLED',
        //TODO change canceled to cancelled in next iteration of primavera theme
        'tp-canceled-not-claimed': phaseState === 'NOT_CLAIMED' && sectionStatus === 'CANCELLED'
      }
    );
    let content: React.ReactElement<HTMLLIElement> = <></>;

    switch (phaseState) {
      case 'FAILED':
      case 'IN_PROGRESS':
      case 'ON_HOLD':
      case 'CANCELLED':
        content = (
          <>
            {this.translate(`phaseStatuses.${phaseState}`)}
          </>
        );
        break;
      case 'COMPLETED':
        if (lastPhase) {
          content = <>{this.translate(`phaseStatuses.${phaseState}`)}</>;
        }
        break;
      default:
        break;
    }

    return (
      <Container
        alignItemsCenter
        className={`tp-submission-status-card-status ${phaseCardStatusCls}`}
        flex1
        hBox
        justifyContentEnd
      >
        <Container hBox alignItemsCenter className="k-text-right tp-text-medium tp-font-size-sm">
          {content}
        </Container>
      </Container>
    );
  }

  renderBody() {
    const { phase, index } = this.props;
    const { phaseOverDue } = this.state;

    let phaseState: string = getSubmissionPhaseStatus(phase);

    const phaseCardCls: string = classNames(
      'tp-submission-status-card',
      {
        'tp-overdue': phaseOverDue && phaseState === 'IN_PROGRESS'
      }
    );

    return (
      <Container
        className={phaseCardCls}
        vBox
        role={'row'}
        key={`tp-submission-status-subsection-${index}`}
      >
        <Container
          hBox
          alignItemsEnd
          role="gridcell"
          className={'tp-submission-status-card-name tp-text-semibold'}
        >
          <span>
            {_.capitalize(phase.phaseName)}
          </span>
        </Container>
        <HBox justifyContentBetween>
          <Container
            alignItemsCenter
            className="tp-submission-status-card-date"
            hBox
            role="gridcell"
          >
            {this.getStatusDate()}
          </Container>
          {this.statusFilesRenderer()}
        </HBox>
        <Container role="gridcell" hBox alignItemsCenter>
          {this.statusContent(phase)}
        </Container>
        <Container
          role={'gridcell'}
          alignItemsCenter
          className={'tp-submission-status-card-assignee'}
          hBox
        >
          {phaseState !== 'SKIPPED' ? this.getAssignee(phase) : this.translate(`phaseStatuses.${phaseState}`)}
        </Container>
        <PuzzleTooltip
          ref={(el: any) => this.tooltip = el}
          anchorElement="target"
          openDelay={1000}
          position="right"
        />
      </Container>
    );
  }
}

export default React.memo(StatusCard);
